/**
 * Column definitions for the "Potwierdź sprzedaż" dialog's nośnik breakdown
 * table (sale-breakdown-not-accepted-units / "Sale action in the offer
 * workspace"). The mirrored columns reuse `buildUnitsTabColumns` exactly —
 * same set, same order, same persisted preferences
 * (`OFFER_TABLE_COLUMN_STORAGE_PREFIX`) — so a nośnik's identifying details
 * never look different in this read-only confirmation than they do in the
 * offer's own units tab (proposal.md's "reuse the existing per-user column
 * selection" decision).
 *
 * Excluded from the mirrored set: the checkbox column (auto-omitted via
 * `canEdit: false`) and every cost/price column (this dialog is about which
 * nośniki close, not what they cost).
 *
 * Three dialog-specific lead columns (outcome, periods, reservation status)
 * are prepended — they are the whole reason this dialog exists.
 */
import * as React from 'react';
import { Badge } from '@ooh/ui/components/badge';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import type { TFunction } from 'i18next';
import type { ReservationStatus } from '@ooh/contracts';
import { formatDisplayDateRange } from '../../../lib/date-format';
import { buildOrderedVisibleColumns } from '../../../lib/ordered-visible-columns';
import {
  buildUnitsTabColumns,
  useOfferTableColumnPreferences,
} from '../units-tab/units-tab-columns';
import type { OfferUnitItem } from '../units-tab/units-tab-columns';
import { OFFER_UNIT_COST_COLUMN_IDS } from '../units-tab/lib/offer-unit-cost-columns';
import { OFFER_UNIT_PRICE_COLUMN_IDS } from '../units-tab/lib/offer-unit-price-columns';
import { ReservationStatusBadge } from '@ooh/screens';

export interface SaleBreakdownPeriod {
  from: Date;
  to: Date;
}

export interface SaleBreakdownRow extends OfferUnitItem {
  currentlyAccepted: boolean;
  periods: SaleBreakdownPeriod[];
  reservationStatus: ReservationStatus;
}

export type SaleRowOutcome = 'alreadySold' | 'willClose' | 'skipped';

/**
 * A row already reported as SOLD by the sale-preview endpoint is settled —
 * mark-sold always reports it `already_sold` regardless of acceptance state
 * or the include-not-currently-accepted opt-in, so it must never be
 * presented as "will close" or counted toward the closing count.
 */
export function getSaleRowOutcome(
  row: SaleBreakdownRow,
  includeNotCurrentlyAccepted: boolean,
): SaleRowOutcome {
  if (row.reservationStatus === 'SOLD') return 'alreadySold';
  return row.currentlyAccepted || includeNotCurrentlyAccepted ? 'willClose' : 'skipped';
}

/** Tints a row skipped for not being currently accepted, for a quick visual scan. */
export function getSaleBreakdownRowClassName(
  row: SaleBreakdownRow,
  includeNotCurrentlyAccepted: boolean,
): string | undefined {
  return getSaleRowOutcome(row, includeNotCurrentlyAccepted) === 'skipped'
    ? 'bg-warning/10'
    : undefined;
}

// Never shown in this read-only confirmation: bulk-selection (auto-omitted via
// canEdit: false below) and every cost/price column (this dialog is about
// which nośniki close, not what they cost — proposal.md decision).
const EXCLUDED_MIRRORED_COLUMN_IDS = new Set<string>([
  ...OFFER_UNIT_COST_COLUMN_IDS,
  ...OFFER_UNIT_PRICE_COLUMN_IDS,
]);

const NO_SELECTED_IDS: Set<OfferUnitItem['id']> = new Set();
function noopOnCheckedChange() {
  // Read-only dialog — the checkbox column is excluded, this is never invoked.
}
function noopOnNoteBlur() {
  // Read-only dialog — notes are not editable here.
}
function noopOnPriceChange() {
  // Read-only dialog — price columns are excluded, this is never invoked.
}

function buildLeadColumns(
  t: TFunction<'offers'>,
  includeNotCurrentlyAccepted: boolean,
): ColumnDef<SaleBreakdownRow>[] {
  return [
    {
      id: 'outcome',
      label: t('workspace.header.saleDialog.columns.outcome'),
      align: 'left',
      width: 150,
      renderCell: (row) => {
        const outcome = getSaleRowOutcome(row, includeNotCurrentlyAccepted);
        if (outcome === 'alreadySold') {
          return (
            <Badge variant="info">{t('workspace.header.saleDialog.outcome.alreadySold')}</Badge>
          );
        }
        return outcome === 'willClose' ? (
          <Badge variant="success">{t('workspace.header.saleDialog.outcome.willClose')}</Badge>
        ) : (
          <Badge variant="warning">{t('workspace.header.saleDialog.outcome.skipped')}</Badge>
        );
      },
    },
    {
      id: 'periods',
      label: t('workspace.header.saleDialog.columns.periods'),
      align: 'left',
      width: 190,
      renderCell: (row) => (
        <div className="flex flex-col gap-0.5">
          {row.periods.map((period) => (
            <span
              key={`${period.from.toISOString()}-${period.to.toISOString()}`}
              className="whitespace-nowrap tabular-nums text-xs"
            >
              {formatDisplayDateRange(period.from, period.to)}
            </span>
          ))}
        </div>
      ),
    },
    {
      id: 'reservationStatus',
      label: t('workspace.header.saleDialog.columns.reservationStatus'),
      align: 'left',
      width: 130,
      resizable: false,
      renderCell: (row) => (
        <ReservationStatusBadge
          status={row.reservationStatus}
          statusLabel={t(`status.${row.reservationStatus}`, { ns: 'reservations' })}
        />
      ),
    },
  ];
}

/**
 * Builds the dialog's full column list: the 3 dialog-specific lead columns,
 * then the caller's own configured offer-table columns (same storage
 * namespace as the units tab), minus cost/price columns.
 */
export function useRecordSaleColumns(
  t: TFunction<'offers'>,
  includeNotCurrentlyAccepted: boolean,
): ColumnDef<SaleBreakdownRow>[] {
  const offerItemColumns = React.useMemo(
    () =>
      buildUnitsTabColumns({
        canEdit: false,
        selectedIds: NO_SELECTED_IDS,
        onCheckedChange: noopOnCheckedChange,
        onNoteBlur: noopOnNoteBlur,
        // supplier.view masking is decided per row (row.supplierWithheld), not by
        // this flag — see units-shared-fields.tsx's own doc comment — safe to
        // hardcode true for this read-only mirror.
        canViewSupplierDetails: true,
        price: { canEdit: false, overrides: new Map(), onPriceChange: noopOnPriceChange },
        t,
      }),
    [t],
  );
  const allColumnIds = React.useMemo(() => offerItemColumns.map((c) => c.id), [offerItemColumns]);
  const { visibleColumnIds, columnOrder, columnWidths } =
    useOfferTableColumnPreferences(allColumnIds);

  const mirroredColumns = React.useMemo(() => {
    const { visibleColumns } = buildOrderedVisibleColumns(
      offerItemColumns,
      columnOrder,
      visibleColumnIds,
      columnWidths,
    );
    return visibleColumns.filter((c) => !EXCLUDED_MIRRORED_COLUMN_IDS.has(c.id));
  }, [offerItemColumns, columnOrder, visibleColumnIds, columnWidths]);

  return React.useMemo(
    // buildUnitsTabColumns returns ColumnDef<OfferUnitItem>[]; SaleBreakdownRow
    // extends OfferUnitItem, and every ColumnDef<T> member uses T only in
    // argument position (renderCell/cellClassName/cellStyle), so a column built
    // for the wider OfferUnitItem type safely accepts the narrower
    // SaleBreakdownRow rows DataTable actually passes it here.
    () => [
      ...buildLeadColumns(t, includeNotCurrentlyAccepted),
      ...(mirroredColumns as ColumnDef<SaleBreakdownRow>[]),
    ],
    [mirroredColumns, t, includeNotCurrentlyAccepted],
  );
}
